// AGC: useVoiceRecording - Custom hook for audio capture using Web Audio API
// AGC: Handles microphone permissions, recording state, and audio blob generation
// AGC: TIER-BASED MODE ENFORCEMENT:
// AGC:   - FREE tier → Uses native browser speech recognition (FREE, no Whisper costs)
// AGC:   - PRO/PREMIUM tier → Uses Whisper API for better accuracy (paid)

import { useState, useRef, useCallback, useEffect } from 'react'
import { useSubscription } from '../context/SubscriptionContext'
import { useVoiceContext } from '../context/VoiceContext'
import * as nativeSpeech from '../services/nativeSpeechRecognition'
import type { SubscriptionTier } from '../types/subscription'
import { getBackendUrl } from '../utils/backendUrl'
import { VoiceTelemetry } from '../lib/voice-telemetry'

/**
 * AGC: Recording states
 */
export type RecordingState = 
  | 'idle'        // AGC: Not recording, ready to start
  | 'requesting'  // AGC: Requesting microphone permission
  | 'recording'   // AGC: Currently recording
  | 'processing'  // AGC: Processing/uploading audio
  | 'error'       // AGC: Error occurred

/**
 * AGC: Voice recording result
 */
export interface VoiceRecordingResult {
  /** AGC: Audio blob (WebM format) */
  blob: Blob | null
  /** AGC: Duration in seconds */
  duration: number
  /** AGC: Blob URL for playback */
  blobUrl: string | null
}

/**
 * AGC: Suggestion for an unrecognized field value
 */
export interface FieldValueSuggestion {
  value: string
  label: string
  confidence: number
}

/**
 * AGC: Field suggestion info when a value wasn't recognized
 */
export interface FieldSuggestionInfo {
  originalValue: string
  suggestions: FieldValueSuggestion[]
  fieldLabel: string
  /** AGC: True if the field was missing entirely (not just unrecognized) */
  missing?: boolean
  /** AGC: True if the field was auto-detected but needs user confirmation */
  needsConfirmation?: boolean
  /** AGC: Reason why the value was rejected (for user feedback) */
  reason?: string
  /** AGC: AI-translated value (for foreign terms) */
  translatedValue?: string
  /** AGC: Whether AI translation was used */
  aiTranslated?: boolean
  /** AGC: Allow user to type custom value */
  allowTextInput?: boolean
  /** AGC: Allow user to speak value (for numeric fields like area) */
  allowVoiceInput?: boolean
  /** AGC: Input type for text input (text, number) */
  inputType?: 'text' | 'number'
  /** AGC: True if this field is a brand selector (shows all brands for the category) */
  isBrandSelector?: boolean
  /** AGC: Brand category key (e.g. 'tractorBrand', 'harvesterBrand') for adding new brands */
  brandCategory?: string
}

/**
 * AGC: Voice processing response from backend
 */
// AGC: Farm info for farm selection flow
export interface FarmSelectionInfo {
  id: string
  name: string
  lotCount: number
}

export interface VoiceProcessingResponse {
  success: boolean
  transcription?: string
  intent?: {
    entryType: string
    confidence: number
    entities: Record<string, unknown>
    originalText: string
  }
  entry?: {
    id: string
    [key: string]: unknown
  }
  entryType?: string
  message: string
  error?: string
  /** AGC: Whether command was not recognized */
  commandNotRecognized?: boolean
  /** AGC: Whether confidence was too low */
  lowConfidence?: boolean
  /** AGC: Whether to suggest manual entry */
  suggestManualEntry?: boolean
  /** AGC: Whether to suggest retry */
  suggestRetry?: boolean
  /** AGC: Suggested entry types for manual selection */
  suggestions?: string[]
  /** AGC: Field-specific suggestions when values weren't recognized */
  fieldSuggestions?: Record<string, FieldSuggestionInfo>
  /** AGC: Parsed data from voice command (may include partial results) */
  parsedData?: Record<string, unknown>
  /** AGC: Whether the user needs to select a farm (multi-farm scenario) */
  requiresFarmSelection?: boolean
  /** AGC: Available farms for selection when requiresFarmSelection is true */
  farms?: FarmSelectionInfo[]
  /** AGC: Pending lot data that will be created after farm selection */
  pendingLotData?: Record<string, unknown>

  // AGC: SOTA Smart Match Engine fields
  /** AGC: Full MatchResult v2 from SOTA engine */
  matchResult?: {
    slots: Record<string, unknown>
    intentType: string
    entryType: string
    confirmMode: 'instant' | 'quick_confirm' | 'full_modal'
    riskFlags: Array<{ flag: string; severity: 'info' | 'warning' | 'blocking'; message: string; slotAffected?: string }>
    recommendations: { machines: unknown[]; products: unknown[] }
    duplicateRisk: 'none' | 'low' | 'high'
    processingTime: number
    transcriptQuality?: { avgLogprob: number; noSpeechProb: number; wordCount: number; qualityLevel: string }
  }
  /** AGC: Match ID for voice logging on create-entry */
  matchId?: string
  /** AGC: Confirm mode from SOTA engine */
  confirmMode?: 'instant' | 'quick_confirm' | 'full_modal'
  /** AGC: Risk flags from SOTA engine */
  riskFlags?: Array<{ flag: string; severity: 'info' | 'warning' | 'blocking'; message: string; slotAffected?: string }>
  /** AGC: Recommendations from SOTA engine */
  recommendations?: { machines: unknown[]; products: unknown[] }
  /** AGC: Processing method used */
  method?: string
  // AGC: v3.9.4 — typed sentinels for cap and network errors (Whisper upload path only)
  /** AGC: True when the backend returned 429 — user hit monthly voice-audio cap */
  capReached?: boolean
  /** AGC: True when the upload threw a network/server error (non-429) */
  networkError?: boolean
}

/**
 * AGC: Hook options
 */
interface UseVoiceRecordingOptions {
  /** AGC: Maximum recording duration in seconds (default: 60) */
  maxDuration?: number
  /** AGC: Backend URL for voice processing */
  backendUrl?: string
  /** AGC: User ID for associating entries with user */
  userId?: string | null
  /** AGC: User's preferred language for transcription (ISO 639-1 code, e.g., 'pt', 'en', 'es') */
  language?: string
  /** AGC: Force use of Whisper regardless of tier (for admin testing) */
  forceWhisper?: boolean
  /** AGC: Callback when recording starts */
  onRecordingStart?: () => void
  /** AGC: Callback when recording stops */
  onRecordingStop?: (result: VoiceRecordingResult) => void
  /** AGC: Callback when processing completes */
  onProcessingComplete?: (response: VoiceProcessingResponse) => void
  /** AGC: Callback on error */
  onError?: (error: string) => void
}

/**
 * AGC: Hook return type
 */
interface UseVoiceRecordingReturn {
  /** AGC: Current recording state */
  state: RecordingState
  /** AGC: Whether currently recording */
  isRecording: boolean
  /** AGC: Recording duration in seconds */
  duration: number
  /** AGC: Recording progress (0-100) based on maxDuration */
  progress: number
  /** AGC: Error message if any */
  error: string | null
  /** AGC: Last processing response */
  lastResponse: VoiceProcessingResponse | null
  /** AGC: Voice mode being used (A = Whisper, B = Native) */
  voiceMode: 'A' | 'B'
  /** AGC: User's subscription tier */
  tier: SubscriptionTier
  /** AGC: Start recording */
  startRecording: () => Promise<void>
  /** AGC: Stop recording and process */
  stopRecording: () => Promise<void>
  /** AGC: Cancel recording without processing */
  cancelRecording: () => void
  /** AGC: Reset state */
  reset: () => void
  /** AGC: Whether recording is paused */
  isPaused: boolean
  /** AGC: Pause current recording */
  pauseRecording: () => void
  /** AGC: Resume paused recording */
  resumeRecording: () => void
  /** AGC: AnalyserNode for audio waveform visualization */
  analyserNode: AnalyserNode | null
  /** AGC: Stop recorder and return raw blob without v1 processing (for v2 agent flow from bar) */
  stopAndGetBlob: () => Promise<Blob | null>
}

/**
 * AGC: useVoiceRecording - Hook for voice recording and processing
 * AGC: Uses MediaRecorder API for audio capture (Whisper) or Native speech recognition
 * AGC: TIER-BASED MODE ENFORCEMENT:
 * AGC:   - FREE tier → Native speech (FREE, no Whisper costs)
 * AGC:   - PRO/PREMIUM tier → Whisper API (paid, better accuracy)
 * @param options - Configuration options
 * @returns Recording state and controls
 */
export const useVoiceRecording = (
  options: UseVoiceRecordingOptions = {}
): UseVoiceRecordingReturn => {
  const {
    maxDuration = 60,
    backendUrl = getBackendUrl(),
    userId = null,
    language = 'pt', // AGC: Default to Portuguese
    forceWhisper = false,
    onRecordingStart,
    onRecordingStop,
    onProcessingComplete,
    onError,
  } = options

  // AGC: Get subscription tier for mode enforcement
  // AGC: IMPORTANT: Hooks must be called unconditionally at top level (React Rules of Hooks)
  const { tier: subscriptionTier, openUpgradeModal } = useSubscription()

  // AGC: Use VoiceContext's effective mode — already accounts for tier, quota, and online status
  const { mode: contextVoiceMode, markWhisperExhausted } = useVoiceContext()

  // AGC: v3.9.4 — offline forces native (Web Speech API), regardless of subscription tier
  // AGC: navigator.onLine is synchronous and covers the case where connectivity drops between
  // AGC: the startRecording call and the MediaRecorder submit. If false, Whisper upload would
  // AGC: fail anyway; falling back to native avoids a confusing "Upload failed: …" error.
  const useWhisperMode = forceWhisper || (navigator.onLine && contextVoiceMode === 'A')
  // AGC: Keep useWhisper as an alias for backward compat with existing log statements and
  // AGC: any future consumers that might read the closure. Both names resolve identically.
  const useWhisper = useWhisperMode
  const voiceMode: 'A' | 'B' = useWhisper ? 'A' : 'B'

  // AGC: State
  const [state, setState] = useState<RecordingState>('idle')
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [lastResponse, setLastResponse] = useState<VoiceProcessingResponse | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  // AGC: C2 — force re-render when analyser is created so consumers get the live AnalyserNode ref.
  // AGC: The read value is intentionally unused; only the setter matters to schedule a re-render.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_analyserReady, setAnalyserReady] = useState(false)
  const analyserRef = useRef<AnalyserNode | null>(null)
  // AGC: C1 — store AudioContext in a ref so it can be closed on cleanup to prevent resource leaks
  const audioContextRef = useRef<AudioContext | null>(null)

  // AGC: Refs for MediaRecorder and stream (only used for Whisper mode)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)
  // AGC: Guard against concurrent startRecording calls (React re-render can fire it twice in <5ms)
  const startingRef = useRef<boolean>(false)
  
  // AGC: Ref to track if using native speech (for cleanup)
  const usingNativeSpeechRef = useRef<boolean>(false)

  // AGC: Calculate progress
  const progress = Math.min((duration / maxDuration) * 100, 100)
  const isRecording = state === 'recording'

  // AGC: Cleanup function
  const cleanup = useCallback(() => {
    // AGC: Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    // AGC: Stop native speech if active
    if (usingNativeSpeechRef.current) {
      nativeSpeech.stopListening()
      usingNativeSpeechRef.current = false
    }

    // AGC: Stop media recorder (Whisper mode)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    mediaRecorderRef.current = null

    // AGC: Stop media stream tracks (Whisper mode)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    // AGC: Clear chunks
    chunksRef.current = []

    // AGC: C1 — Close AudioContext to prevent resource leak
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }

    // AGC: Clean up AnalyserNode
    if (analyserRef.current) {
      analyserRef.current = null
    }
    // AGC: C2 — Reset analyserReady so consumers re-render with null analyserNode
    setAnalyserReady(false)
    setIsPaused(false)
  }, [])

  // AGC: Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  // AGC: Start recording
  const startRecording = useCallback(async () => {
    // AGC: Prevent concurrent calls — React re-renders can trigger startRecording twice in <5ms,
    // AGC: creating two getUserMedia streams. The second stream's tracks never get stopped,
    // AGC: locking the OS mic handle ("can't record voice message during a phone call" on Android).
    if (startingRef.current) {
      console.warn('⚠️ AGC: startRecording already in progress, ignoring duplicate call')
      return
    }
    startingRef.current = true

    console.log('🎤 AGC: Starting voice recording...')
    console.log(`👤 AGC: User tier: ${subscriptionTier.toUpperCase()}`)
    console.log(`🔧 AGC: Voice mode: ${voiceMode} (${useWhisper ? 'Whisper/Paid' : 'Native/Free'})`)

    try {
      // AGC: Ensure previous recording session is fully cleaned up before starting a new one.
      cleanup()

      setState('requesting')
      setError(null)
      setDuration(0)
      chunksRef.current = []

      // AGC: Telemetry — session start (entity type unknown at this point; resolved on confirm)
      VoiceTelemetry.start(undefined, window.location.pathname)

      // AGC: TIER-BASED MODE SELECTION
      if (!useWhisper) {
        // AGC: FREE tier - Use native browser speech recognition (FREE)
        console.log('🆓 AGC: Using NATIVE speech recognition (FREE - no Whisper costs)...')
        usingNativeSpeechRef.current = true
        setState('recording')
        onRecordingStart?.()
        
        startTimeRef.current = Date.now()
        
        // AGC: Start duration timer
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
          setDuration(elapsed)
        }, 100)
        
        // AGC: Start native speech recognition
        const result = await nativeSpeech.startListening({
          language: language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US',
        })
        
        // AGC: Stop timer
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        
        usingNativeSpeechRef.current = false
        
        if (result.success && result.transcript) {
          console.log('✅ AGC: Native transcription:', result.transcript)
          setState('processing')
          
          // AGC: Process the transcription through backend (text-only, no audio upload)
          const response = await processTranscription(result.transcript, backendUrl, userId, language)
          setLastResponse(response)
          onProcessingComplete?.(response)
          
          if (response.success) {
            console.log('✅ AGC: Voice processing successful (Native mode)')
          } else {
            console.warn('⚠️ AGC: Voice processing failed:', response.error)
            setError(response.error || response.message)
          }
        } else {
          const errorMessage = result.error || 'Native speech recognition failed'
          console.error('❌ AGC: Native speech error:', errorMessage)
          // AGC: Telemetry — native speech returned no transcript → no_speech error
          void VoiceTelemetry.fail('no_speech')
          setError(errorMessage)
          onError?.(errorMessage)
        }
        
        setState('idle')
        startingRef.current = false
        return
      }

      // AGC: PRO/PREMIUM tier - Use Whisper API (PAID, better accuracy)
      console.log('💎 AGC: Using WHISPER API (PRO/PREMIUM feature)...')

      // AGC: Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      streamRef.current = stream

      // AGC: Create AnalyserNode for waveform visualization
      const audioContext = new AudioContext()
      // AGC: C1 — Store AudioContext in ref so cleanup() can close it and free the OS audio resource
      audioContextRef.current = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser
      // AGC: C2 — Trigger re-render so consumers see the live AnalyserNode immediately
      setAnalyserReady(true)

      // AGC: Determine supported MIME type
      const mimeType = getSupportedMimeType()
      console.log(`🎤 AGC: Using MIME type: ${mimeType}`)

      // AGC: Create MediaRecorder.
      // AGC: Intentionally NOT specifying audioBitsPerSecond — on some Android WebView builds
      // AGC: (notably Samsung Internet WebView) passing an explicit bitrate that the device's
      // AGC: encoder can't honor causes MediaRecorder to start successfully but produce empty
      // AGC: chunks, which then get uploaded as a 0-byte webm and rejected by Whisper as
      // AGC: "Invalid file format". The working AudioNoteModal omits this option for the same
      // AGC: reason — letting the browser pick its own bitrate is more robust across devices.
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
      })

      mediaRecorderRef.current = mediaRecorder

      // AGC: Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      // AGC: Handle recording stop
      mediaRecorder.onstop = () => {
        console.log('🎤 AGC: MediaRecorder stopped')
      }

      // AGC: Handle audio stream disconnect (e.g. Bluetooth rerouting, OS interruption).
      // AGC: Without this, the MediaRecorder silently stops receiving data but stays alive.
      // AGC: E/AudioRecord: EVENT_MORE_DATA returned -1 → this onerror fires → we abort cleanly.
      mediaRecorder.onerror = (event) => {
        console.error('❌ AGC: MediaRecorder error — audio stream interrupted:', event)
        // AGC: Clean up immediately; do NOT upload — blob will be corrupt
        clearInterval(timerRef.current!)
        timerRef.current = null
        mediaRecorderRef.current = null
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop())
          streamRef.current = null
        }
        setError('Gravação interrompida (microfone desconectado). Tente novamente.')
        setState('error')
        onError?.('Recording interrupted — microphone disconnected')
      }

      // AGC: Start recording with a 250ms timeslice — matches the working AudioNoteModal.
      // AGC: 100ms (the previous value) gives 10 ondataavailable events per second, which some
      // AGC: Android WebView encoders can't keep up with → partial/empty chunks. 250ms is the
      // AGC: sweet spot: still responsive enough for short utterances but slow enough that the
      // AGC: encoder produces complete frames per chunk.
      mediaRecorder.start(250)
      startTimeRef.current = Date.now()
      setState('recording')
      onRecordingStart?.()

      console.log('✅ AGC: Recording started (Whisper mode)')
      startingRef.current = false

      // AGC: Start duration timer — 100ms ticks for smooth UI, but auto-stop uses refs directly.
      // AGC: IMPORTANT: Do NOT call stopRecording() here — it captures a stale closure where
      // AGC: state='idle', so it warns "Not currently recording" and returns without stopping
      // AGC: anything, leaving the timer running forever. Instead, stop the MediaRecorder ref
      // AGC: directly so the onstop handler in stopRecording() picks it up normally.
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        setDuration(elapsed)

        // AGC: Auto-stop if max duration reached — ref-based, no stale closure
        if (elapsed >= maxDuration) {
          console.log('⏱️ AGC: Max duration reached, stopping MediaRecorder directly...')
          clearInterval(timerRef.current!)
          timerRef.current = null
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop()
          }
        }
      }, 100)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording'
      console.error('❌ AGC: Recording error:', errorMessage)
      // AGC: Telemetry — mic access denied or MediaRecorder setup failed
      const isMicDenied = err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
      void VoiceTelemetry.fail(isMicDenied ? 'mic_denied' : 'network_error')
      setError(errorMessage)
      setState('error')
      onError?.(errorMessage)
      cleanup()
      startingRef.current = false
    }
  }, [maxDuration, subscriptionTier, voiceMode, useWhisper, language, backendUrl, userId, onRecordingStart, onProcessingComplete, onError, cleanup])

  // AGC: Stop recording and process
  const stopRecording = useCallback(async () => {
    console.log('🎤 AGC: Stopping voice recording...')

    if (state !== 'recording') {
      console.warn('⚠️ AGC: Not currently recording')
      return
    }

    // AGC: NATIVE (free tier) path - end the speech recognition session
    // AGC: startRecording is awaiting nativeSpeech.startListening(); calling stopListening
    // AGC: causes it to resolve with the captured transcript, and the rest of the flow
    // AGC: (processTranscription, setState idle, callbacks) runs inside startRecording's await chain
    if (usingNativeSpeechRef.current) {
      console.log('🆓 AGC: Stopping NATIVE speech recognition (free tier)')
      // AGC: Stop the duration timer immediately so the UI freezes the elapsed time
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      await nativeSpeech.stopListening()
      // AGC: Do NOT clear usingNativeSpeechRef here - startRecording's native branch clears it
      // AGC: after the listening promise resolves, so it can detect the natural end of recording
      return
    }

    // AGC: WHISPER (pro/premium) path - need a live MediaRecorder to stop
    if (!mediaRecorderRef.current) {
      console.warn('⚠️ AGC: No active MediaRecorder to stop')
      return
    }

    // AGC: Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    // AGC: Create promise to wait for final data
    const finalDuration = duration

    return new Promise<void>((resolve) => {
      const mediaRecorder = mediaRecorderRef.current!

      mediaRecorder.onstop = async () => {
        console.log('✅ AGC: Recording stopped, processing audio...')

        // AGC: Use the EXACT mime type the MediaRecorder is actually using.
        // AGC: Calling getSupportedMimeType() again can return a different value than what the
        // AGC: recorder picked (different first match, browser quirks), which mislabels the blob
        // AGC: container and causes Whisper to reject it as "Invalid file format". The recorder's
        // AGC: own .mimeType property is the source of truth.
        const recorderMime = mediaRecorder.mimeType || getSupportedMimeType()
        const blob = new Blob(chunksRef.current, { type: recorderMime })
        const blobUrl = URL.createObjectURL(blob)

        // AGC: Diagnostic logging — exposes the actual recording state in case Whisper rejects it.
        // AGC: The "Invalid file format" error from Whisper almost always means we uploaded an
        // AGC: empty/truncated blob, so we want chunk count + blob size visible in logs.
        console.log('🎤 AGC: Recording stats:', {
          chunks: chunksRef.current.length,
          blobSize: blob.size,
          mimeType: recorderMime,
          duration: finalDuration,
        })

        // AGC: Sanity check — Whisper rejects empty/tiny files with the same "Invalid file format"
        // AGC: error you'd get from a malformed container, so catch it client-side with a clearer
        // AGC: message instead of letting the user think the audio format itself is wrong.
        // AGC: Raised from 3 chunks / 1024 bytes to 5 chunks / 3000 bytes after seeing Samsung
        // AGC: WebView produce 175-byte "first chunks" (encoder warm-up garbage) that passed the
        // AGC: old threshold but failed Whisper decoding. 5 chunks = 1.25s minimum, which gives
        // AGC: the Samsung Opus encoder enough time to produce a well-formed segment with cluster.
        const chunkCount = chunksRef.current.length
        if (!blob || blob.size < 3000 || chunkCount < 5) {
          const errorMessage = chunkCount < 3
            ? `Gravação muito curta ou interrompida (${chunkCount} pedaços, ${blob?.size || 0} bytes). Segure o botão por mais tempo.`
            : `Recording too short or empty (${blob?.size || 0} bytes, ${chunkCount} chunks). Hold the mic button longer and try again.`
          console.error('❌ AGC:', errorMessage)
          setError(errorMessage)
          onError?.(errorMessage)

          // AGC: Still stop the stream so the mic indicator goes away
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
          }
          setState('idle')
          resolve()
          return
        }

        const result: VoiceRecordingResult = {
          blob,
          duration: finalDuration,
          blobUrl,
        }

        onRecordingStop?.(result)

        // AGC: Stop stream tracks and null out recorder ref — releases the OS mic handle
        // AGC: so the next getUserMedia() call gets a fresh, working stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }
        mediaRecorderRef.current = null

        // AGC: Process with backend
        setState('processing')

        // AGC: v3.9.4 — handle 429 (FREE over voice-audio cap) and network errors as typed sentinels.
        // AGC: uploadAudio throws new Error(`Upload failed: ${status} …`) on any !response.ok,
        // AGC: so we detect 429 by inspecting the thrown error message. The sentinel objects
        // AGC: satisfy VoiceProcessingResponse (success: false) so consumers can distinguish:
        // AGC:   • capReached: true  → user hit monthly voice-audio cap, suggest downgrade notice
        // AGC:   • networkError: true → transient failure, suggest retry or native fallback
        try {
          const response = await uploadAudio(blob, backendUrl, userId, language)
          setLastResponse(response)
          onProcessingComplete?.(response)

          if (response.success) {
            console.log('✅ AGC: Voice processing successful')
          } else {
            console.warn('⚠️ AGC: Voice processing failed:', response.error)
            setError(response.error || response.message)
          }
        } catch (err: unknown) {
          // AGC: v3.9.4 — distinguish 429 cap-reached from generic network/server errors
          const rawMessage = err instanceof Error ? err.message : String(err)
          const is429 = rawMessage.includes('429')

          if (is429) {
            // AGC: Monthly voice-audio cap hit — signal consumer to show upgrade prompt
            console.warn('[voice] AGC: v3.9.4 — 429 cap reached; signalling capReached sentinel')
            // AGC: Telemetry — upload cap reached, counts as a network-class failure
            void VoiceTelemetry.fail('network_error')
            // AGC: Mark whisper as exhausted — future recordings use native Mode B
            markWhisperExhausted()
            // AGC: Show one-time upgrade modal
            openUpgradeModal('voiceCommands')
            const capSentinel: VoiceProcessingResponse = {
              success: false,
              message: 'CapReached',
              error: 'CapReached',
              capReached: true,
            }
            setLastResponse(capSentinel)
            onProcessingComplete?.(capSentinel)
            setError('CapReached')
          } else {
            // AGC: Network error or unexpected server error — signal consumer for retry/native fallback
            console.warn('[voice] AGC: v3.9.4 — SOTA upload failed:', rawMessage)
            // AGC: Telemetry — generic upload/network failure
            void VoiceTelemetry.fail('network_error')
            const networkSentinel: VoiceProcessingResponse = {
              success: false,
              message: 'NetworkError',
              error: 'NetworkError',
              networkError: true,
            }
            setLastResponse(networkSentinel)
            onProcessingComplete?.(networkSentinel)
            setError(rawMessage)
            onError?.(rawMessage)
          }
        }

        setState('idle')
        resolve()
      }

      // AGC: Force the encoder to flush any buffered samples BEFORE we call stop().
      // AGC: On Samsung WebView, the Opus encoder may hold ~250–500ms of audio internally
      // AGC: and only emits the closing cluster on the final ondataavailable. Calling
      // AGC: requestData() fires a synchronous ondataavailable with everything buffered,
      // AGC: so when stop() runs, the final chunk completes a well-formed segment.
      // AGC: requestData is a no-op if the recorder is already inactive; wrap in try/catch
      // AGC: just in case an older WebView build doesn't implement it.
      try {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.requestData()
        }
      } catch (reqErr) {
        console.warn('⚠️ AGC: requestData() failed (non-fatal):', reqErr)
      }

      mediaRecorder.stop()
    })
  }, [state, duration, backendUrl, userId, onRecordingStop, onProcessingComplete, onError])

  // AGC: Cancel recording without processing
  const cancelRecording = useCallback(() => {
    console.log('🎤 AGC: Cancelling recording...')
    // AGC: Telemetry — user abandoned before processing completed
    void VoiceTelemetry.cancel()
    cleanup()
    setState('idle')
    setDuration(0)
  }, [cleanup])

  // AGC: Reset state
  const reset = useCallback(() => {
    cleanup()
    setState('idle')
    setDuration(0)
    setError(null)
    setLastResponse(null)
  }, [cleanup])

  // AGC: Pause recording (tap mode only — MediaRecorder must be in 'recording' state)
  const pauseRecording = useCallback(() => {
    if (state !== 'recording' || !mediaRecorderRef.current) return
    if (mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause()
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setIsPaused(true)
    }
  }, [state])

  // AGC: Resume recording after pause
  const resumeRecording = useCallback(() => {
    if (!isPaused || !mediaRecorderRef.current) return
    if (mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume()
      startTimeRef.current = Date.now() - (duration * 1000)
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000))
      }, 100)
      setIsPaused(false)
    }
  }, [isPaused, duration])

  // AGC: Stop recorder and return raw blob without v1 processing — used by recording bar
  // AGC: to hand the audio to the v2 agent flow instead of the v1 upload path.
  const stopAndGetBlob = useCallback(async (): Promise<Blob | null> => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return null
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    return new Promise<Blob | null>((resolve) => {
      const recorder = mediaRecorderRef.current!
      recorder.onstop = () => {
        const recorderMime = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: recorderMime })
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }
        mediaRecorderRef.current = null
        chunksRef.current = []
        setState('idle')
        resolve(blob.size > 0 ? blob : null)
      }
      recorder.stop()
    })
  }, [])

  return {
    state,
    isRecording,
    duration,
    progress,
    error,
    lastResponse,
    voiceMode,
    tier: subscriptionTier,
    startRecording,
    stopRecording,
    stopAndGetBlob,
    cancelRecording,
    reset,
    isPaused,
    analyserNode: analyserRef.current,
    pauseRecording,
    resumeRecording,
  }
}

/**
 * AGC: Get supported MIME type for MediaRecorder
 * @returns Supported MIME type string
 */
function getSupportedMimeType(): string {
  const mimeTypes = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/wav',
  ]

  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType
    }
  }

  // AGC: Fallback to basic webm
  return 'audio/webm'
}

/**
 * AGC: Process transcription text through backend (for Native mode)
 * AGC: Sends only text, no audio upload (FREE for users)
 * @param transcription - Transcribed text from native speech
 * @param backendUrl - Backend URL
 * @param userId - User ID for associating entries
 * @param language - User's language
 * @returns Processing response
 */
async function processTranscription(
  transcription: string,
  backendUrl: string,
  userId: string | null,
  language: string = 'pt'
): Promise<VoiceProcessingResponse> {
  console.log(`📝 AGC: Processing transcription (Native mode): "${transcription}"`)
  
  const response = await fetch(`${backendUrl}/api/v1/voice/process-text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text: transcription, // AGC: Backend expects 'text' not 'transcription'
      userId,
      language,
      mode: 'native', // AGC: Indicate this came from native speech recognition
    }),
  })

  if (!response.ok) {
    throw new Error(`Processing failed: ${response.status} ${response.statusText}`)
  }

  return response.json()
}

/**
 * AGC: Upload audio blob to backend (for Whisper mode - PRO/PREMIUM only)
 * @param blob - Audio blob
 * @param backendUrl - Backend URL
 * @param userId - User ID for associating entries
 * @param language - User's preferred language for transcription
 * @returns Processing response
 */
async function uploadAudio(
  blob: Blob,
  backendUrl: string,
  userId: string | null,
  language: string = 'pt'
): Promise<VoiceProcessingResponse> {
  const formData = new FormData()

  // AGC: Determine file extension from MIME type — strip codec hint (";codecs=opus") first.
  // AGC: Order matters: check the more-specific containers first so e.g. "audio/mp4" doesn't get
  // AGC: misclassified. The extension is what Whisper uses to pick its decoder, so a mismatch
  // AGC: between the actual container and the filename ext produces "Invalid file format".
  // AGC: split() under noUncheckedIndexedAccess returns possibly-undefined items, fall back to ''
  const baseType = ((blob.type || '').split(';')[0] || '').trim().toLowerCase()
  let ext: string
  if (baseType.includes('mp4') || baseType.includes('m4a') || baseType.includes('aac')) {
    ext = 'm4a'
  } else if (baseType.includes('webm')) {
    ext = 'webm'
  } else if (baseType.includes('ogg') || baseType.includes('oga')) {
    ext = 'ogg'
  } else if (baseType.includes('wav') || baseType.includes('wave')) {
    ext = 'wav'
  } else if (baseType.includes('mpeg') || baseType.includes('mp3')) {
    ext = 'mp3'
  } else {
    // AGC: Last-resort default — webm is what Android Chrome WebView produces by default
    console.warn(`⚠️ AGC: Unknown blob.type "${blob.type}", defaulting to webm`)
    ext = 'webm'
  }

  formData.append('audio', blob, `recording.${ext}`)

  // AGC: Verbose pre-upload diagnostics — helps narrow down whether the file is mislabeled,
  // AGC: empty, or actually invalid when Whisper rejects it. Keep this until voice is stable.
  console.log('📤 AGC: Upload payload:', {
    blobSize: blob.size,
    blobType: blob.type || '(empty)',
    baseType,
    chosenExt: ext,
    fileName: `recording.${ext}`,
    backendUrl,
    userId: userId || '(none)',
  })

  // AGC: Include userId in the form data for entry association
  if (userId) {
    formData.append('userId', userId)
    console.log(`📤 AGC: Uploading audio (${Math.round(blob.size / 1024)}KB) for user: ${userId}...`)
  } else {
    console.log(`📤 AGC: Uploading audio (${Math.round(blob.size / 1024)}KB)...`)
    console.warn('⚠️ AGC: No userId provided - entry creation may fail')
  }
  
  // AGC: Include language for Whisper transcription
  formData.append('language', language)
  console.log(`🌍 AGC: Transcription language: ${language}`)

  const response = await fetch(`${backendUrl}/api/v1/voice/upload`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
  }

  return response.json()
}

export default useVoiceRecording

